import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ENGAGEMENT_STATUSES, ENGAGEMENT_STAGES, ENGAGEMENT_HEALTHS, BILLING_MODELS } from "@/lib/enumLabels";

const TYPES = [
  {
    value: "trusted_advisor",
    label: "Trusted Advisor",
    blurb: "Flagship ongoing counsel · leadership continuity",
    default_billing: "retainer",
    scope: "Ongoing advisory relationship providing continuing access to executive counsel. Yadid remains available for strategic input as opportunities emerge, partnerships evolve, and significant decisions take shape.",
    objectives: [
      "Strengthen executive judgment on live decisions",
      "Navigate complexity across evolving partnerships and stakeholders",
      "Cultivate strategic relationships that compound over time",
      "Sustain leadership capacity to respond thoughtfully to change",
    ],
    deliverables: [
      "Monthly principal-to-principal advisory session",
      "On-demand counsel on active decisions and inflection points",
      "Quarterly written strategic pulse for the leadership team",
      "Warm introductions to relevant peers and stakeholders",
    ],
  },
  {
    value: "strategic_initiative",
    label: "Strategic Initiative",
    blurb: "Focused advisory around one opportunity",
    default_billing: "fixed",
    scope: "Focused executive advisory supporting a clearly defined opportunity — strategic planning, partnership development, stakeholder engagement, initiative design, or institutional positioning — with milestones flexible enough to respond to how the opportunity evolves.",
    objectives: [
      "Frame the opportunity and align leadership on the win",
      "Design the strategy and sequencing to capture it",
      "Engage the stakeholders whose support unlocks it",
      "Position the institution to sustain the outcome",
    ],
    deliverables: [
      "Diagnostic memo framing the opportunity and its stakes",
      "Strategy brief with recommended sequencing and success measures",
      "Stakeholder engagement map with tailored outreach approach",
      "Board- or leadership-ready presentation of the plan",
    ],
  },
  {
    value: "executive_convening",
    label: "Executive Convening",
    blurb: "Right leaders in the room, right purpose",
    default_billing: "t_and_m",
    scope: "Intentional convening of the leaders whose alignment advances the partner organization's mission — participant strategy, convening design, executive facilitation, stakeholder engagement, and follow-on relationship cultivation.",
    objectives: [
      "Define the purpose that justifies leaders' time in the room",
      "Curate the participant list to maximize institutional outcome",
      "Design a convening arc that builds trust and moves decisions forward",
      "Structure follow-on engagement so commitments become action",
    ],
    deliverables: [
      "Participant strategy: purpose, invite sequence, absence risks",
      "Facilitated agenda arc from shared frame to signed commitments",
      "Live facilitation and read-out of decisions and dissents",
      "Follow-on cultivation plan with 1:1s and momentum tracking",
    ],
  },
  {
    value: "executive_communication",
    label: "Executive Comms & Facilitation",
    blurb: "Prepared voice · facilitated dialogue",
    default_billing: "t_and_m",
    scope: "Preparation and facilitation for high-stakes executive moments — remarks development, message architecture, presentation coaching, and live facilitation of consequential conversations.",
    objectives: [
      "Sharpen the message so the leader's voice lands as intended",
      "Prepare the leader for the moment and its stakeholders",
      "Facilitate the dialogue toward a clear outcome",
      "Debrief and codify what to carry into the next moment",
    ],
    deliverables: [
      "Message architecture: core narrative, proof points, refrains",
      "Speaker prep pack: remarks, Q&A anticipation, coaching notes",
      "Live facilitation of the moment (meeting, board, panel)",
      "Post-moment debrief and lessons carried into the next convening",
    ],
  },
];
const TYPE_LABEL = TYPES.reduce((acc, t) => { acc[t.value] = t.label; return acc; }, {});
const TYPE_BY_VALUE = TYPES.reduce((acc, t) => { acc[t.value] = t; return acc; }, {});

function playbookCompletionPct(e) {
  const book = e?.playbook || e?.convening_playbook;
  if (!book || typeof book !== "object") return null;
  let done = 0, total = 0;
  for (const items of Object.values(book)) {
    if (!Array.isArray(items)) continue;
    for (const it of items) { total += 1; if (it?.done) done += 1; }
  }
  return total === 0 ? null : Math.round((done / total) * 100);
}

const fmt$ = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(1)}M` : n >= 1000 ? `$${(n/1000).toFixed(0)}K` : `$${Math.round(n||0)}`;

export default function Engagements() {
  const [engs, setEngs] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [form, setForm] = useState({
    name: "", client_id: "", engagement_type: "strategic_initiative", status: "scoping", stage: "diagnostic",
    partner_lead: "", start_date: "", target_end_date: "", budget: "", billing_model: "fixed",
    objectives: "", scope_summary: "", key_risks: "", deliverables: "",
  });
  // Track which fields the user has hand-edited so we don't clobber their work when the type changes
  const [dirty, setDirty] = useState({ billing_model: false, objectives: false, scope_summary: false, deliverables: false });

  const applyTypeDefaults = (type_value, current) => {
    const spec = TYPE_BY_VALUE[type_value];
    if (!spec) return current;
    const next = { ...current, engagement_type: type_value };
    if (!dirty.billing_model) next.billing_model = spec.default_billing;
    if (!dirty.objectives) next.objectives = spec.objectives.join("\n");
    if (!dirty.scope_summary) next.scope_summary = spec.scope;
    if (!dirty.deliverables) next.deliverables = spec.deliverables.join("\n");
    return next;
  };

  const load = () => {
    api.get("/engagements").then((r) => setEngs(r.data));
    api.get("/clients").then((r) => setClients(r.data));
  };
  useEffect(() => { load(); }, []);
  // Iter 85.57 · Partner lead picker needs the canonical User list. Mirrors
  // the Clients page: fetched once on mount because the team roster rarely
  // changes during a session. Legacy string `partner_lead` values that
  // don't match any user's name are preserved and still displayed as-is.
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  // Iter 85.56 · Optimistic patch for inline row edits. Reuses the same
  // canonical PUT /engagements/{id} that EngagementDetail's `save` uses so
  // both surfaces share one data model. Reverts on failure and toasts.
  const patchEngagement = async (id, patch) => {
    const prev = engs;
    setEngs((es) => es.map((e) => (e.id === id ? { ...e, ...patch } : e)));
    try {
      const { data } = await api.put(`/engagements/${id}`, patch);
      setEngs((es) => es.map((e) => (e.id === id ? data : e)));
      toast.success("Engagement updated");
    } catch (err) {
      setEngs(prev);
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const submit = async () => {
    if (!form.name || !form.client_id) return toast.error("Name and client required");
    try {
      await api.post("/engagements", {
        ...form,
        budget: form.budget ? parseFloat(form.budget) : null,
        objectives: form.objectives.split("\n").map(s=>s.trim()).filter(Boolean),
        key_risks: form.key_risks.split("\n").map(s=>s.trim()).filter(Boolean),
        deliverables: (form.deliverables || "").split("\n").map(s=>s.trim()).filter(Boolean).map(title => ({ title, status: "in_progress" })),
      });
      setOpen(false);
      toast.success("Engagement created");
      setForm({ name: "", client_id: "", engagement_type: "strategic_initiative", status: "scoping", stage: "diagnostic", partner_lead: "", start_date: "", target_end_date: "", budget: "", billing_model: "fixed", objectives: "", scope_summary: "", key_risks: "", deliverables: "" });
      setDirty({ billing_model: false, objectives: false, scope_summary: false, deliverables: false });
      load();
    } catch { toast.error("Failed to create"); }
  };

  const clientName = (cid) => clients.find(c => c.id === cid)?.name || "—";
  const filtered = engs.filter(e => statusFilter === "all" || e.status === statusFilter);

  // Multi-column sort — each header cycles none → desc → asc → none.
  // Only one column is active at a time (clicking a new column resets the others).
  const [sortKey, setSortKey] = useState("none"); // "none" | "playbook" | "health" | "stage" | "budget" | "status"
  const [sortDir, setSortDir] = useState("desc"); // "desc" | "asc"
  const cycleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir("desc"); return; }
    if (sortDir === "desc") { setSortDir("asc"); return; }
    setSortKey("none");
  };

  // Sort indicator — always occupies the same slot next to the label so
  // the header row stays visually anchored regardless of active column.
  // Active columns show a solid ↓/↑; inactive columns show a faint ↕ so
  // partners still see they're sortable without the glyphs jumping.
  const SortGlyph = ({ colKey }) => {
    const active = sortKey === colKey;
    const glyph = active ? (sortDir === "desc" ? "↓" : "↑") : "↕";
    return (
      <span
        aria-hidden="true"
        className={`ml-1 inline-block w-2 text-[0.65rem] leading-none tabular-nums ${active ? "text-primary" : "text-muted-foreground/40"}`}
      >
        {glyph}
      </span>
    );
  };

  // Sortable header cell — factored so every sortable column gets the
  // exact same spacing, hit-target, alignment, and glyph placement.
  // `align` controls justification of both the header label AND the
  // implicit expectation of the data column below it, so the two stay
  // visually locked. `nowrap` is on by default because short exec
  // labels ("Stage", "Health", "Budget · Billed") should never break.
  const SortableTh = ({ colKey, children, align = "left", testId, className = "" }) => {
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <th className={`px-3 py-3 font-normal align-middle text-${align} ${className}`}>
        <button
          type="button"
          onClick={() => cycleSort(colKey)}
          data-testid={testId}
          className={`inline-flex items-center ${justify} whitespace-nowrap uppercase font-mono tracking-widest text-[0.625rem] text-muted-foreground hover:text-primary transition-colors`}
        >
          {children}
          <SortGlyph colKey={colKey} />
        </button>
      </th>
    );
  };

  const HEALTH_RANK = { red: 0, yellow: 1, green: 2 };
  const STAGE_RANK = { diagnostic: 0, design: 1, implementation: 2, review: 3 };
  const STATUS_RANK = { scoping: 0, active: 1, on_hold: 2, delivered: 3, closed: 4 };
  const cmpNum = (a, b) => a - b;
  const cmpNullable = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;   // nulls always sink
    if (b == null) return -1;
    return a - b;
  };
  const sortedFiltered = sortKey === "none" ? filtered : [...filtered].sort((a, b) => {
    let r = 0;
    if (sortKey === "playbook") r = cmpNullable(playbookCompletionPct(a), playbookCompletionPct(b));
    else if (sortKey === "health") r = cmpNum(HEALTH_RANK[a.health] ?? 99, HEALTH_RANK[b.health] ?? 99);
    else if (sortKey === "stage") r = cmpNum(STAGE_RANK[a.stage] ?? 99, STAGE_RANK[b.stage] ?? 99);
    else if (sortKey === "status") r = cmpNum(STATUS_RANK[a.status] ?? 99, STATUS_RANK[b.status] ?? 99);
    else if (sortKey === "budget") r = cmpNullable(a.budget ?? null, b.budget ?? null);
    return sortDir === "desc" ? -r : r;
  });

  return (
    <PageShell size="default">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Delivery</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Engagements</h1>
          <p className="text-sm text-muted-foreground mt-1">{engs.length} total · {engs.filter(e=>e.status==="active").length} active</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-engagement-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New engagement</Button></DialogTrigger>
          <DialogContent className="max-w-2xl rounded-none bg-card max-h-[90vh] overflow-y-auto" data-testid="new-engagement-dialog">
            <DialogHeader><DialogTitle className="font-display">New engagement</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Name *</Label>
                <Input data-testid="engagement-name" value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Partner Organization *</Label>
                <Select value={form.client_id} onValueChange={(v)=>setForm({...form,client_id:v})}>
                  <SelectTrigger data-testid="engagement-client" className="rounded-none bg-secondary/40 mt-1"><SelectValue placeholder="Select partner organization" /></SelectTrigger>
                  <SelectContent>{clients.map(c=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Engagement model</Label>
                <Select value={form.engagement_type} onValueChange={(v)=>setForm(applyTypeDefaults(v, form))}>
                  <SelectTrigger data-testid="engagement-type" className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{t.label}</span>
                          <span className="text-[0.625rem] text-muted-foreground">{t.blurb}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[0.625rem] text-muted-foreground mt-1 font-mono">Auto-fills billing model, scope, and objectives — override any field to keep your edits.</p></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Status</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGAGEMENT_STATUSES.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Stage</Label>
                <Select value={form.stage} onValueChange={(v)=>setForm({...form,stage:v})}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENGAGEMENT_STAGES.map(t=><SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Partner lead</Label>
                <Input value={form.partner_lead} onChange={(e)=>setForm({...form,partner_lead:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e)=>setForm({...form,start_date:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Target end</Label>
                <Input type="date" value={form.target_end_date} onChange={(e)=>setForm({...form,target_end_date:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Budget ($)</Label>
                <Input type="number" value={form.budget} onChange={(e)=>setForm({...form,budget:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Billing</Label>
                <Select value={form.billing_model} onValueChange={(v)=>{ setDirty({...dirty,billing_model:true}); setForm({...form,billing_model:v}); }}>
                  <SelectTrigger className="rounded-none bg-secondary/40 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{BILLING_MODELS.map(b=><SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Scope summary</Label>
                <Textarea rows={3} value={form.scope_summary} onChange={(e)=>{ setDirty({...dirty,scope_summary:true}); setForm({...form,scope_summary:e.target.value}); }} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Objectives (one per line)</Label>
                <Textarea rows={5} value={form.objectives} onChange={(e)=>{ setDirty({...dirty,objectives:true}); setForm({...form,objectives:e.target.value}); }} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Deliverables (one per line)</Label>
                <Textarea rows={5} value={form.deliverables} onChange={(e)=>{ setDirty({...dirty,deliverables:true}); setForm({...form,deliverables:e.target.value}); }} className="rounded-none bg-secondary/40 mt-1" /></div>
              <div><Label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Key risks (one per line)</Label>
                <Textarea rows={3} value={form.key_risks} onChange={(e)=>setForm({...form,key_risks:e.target.value})} className="rounded-none bg-secondary/40 mt-1" /></div>
            </div>
            <DialogFooter><Button data-testid="engagement-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="rounded-none bg-secondary/40 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {ENGAGEMENT_STATUSES.map(s=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {/*
          Iter 85.37 · Executive polish
          ─────────────────────────────
          The header row uses one shared `SortableTh` component so
          every sortable column has identical spacing, hit-target,
          alignment, and sort-glyph placement.  Column widths are
          hinted per-cell (min-w + max-w on the flex text columns,
          fixed w on the numeric/health columns) rather than via
          `table-fixed` so the browser distributes leftover space
          without letter-clipping short labels or bleeding text
          between adjacent columns at narrower viewports.
        */}
        <table className="w-full text-sm min-w-[960px]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-5 py-3 font-normal align-middle min-w-[220px]">
                <span className="inline-flex items-center whitespace-nowrap uppercase font-mono tracking-widest text-[0.625rem] text-muted-foreground">
                  Engagement
                </span>
              </th>
              <th className="text-left px-3 py-3 font-normal align-middle min-w-[140px]">
                <span className="inline-flex items-center whitespace-nowrap uppercase font-mono tracking-widest text-[0.625rem] text-muted-foreground">
                  Partner
                </span>
              </th>
              <th className="text-left px-3 py-3 font-normal align-middle min-w-[130px]">
                <span className="inline-flex items-center whitespace-nowrap uppercase font-mono tracking-widest text-[0.625rem] text-muted-foreground">
                  Partner lead
                </span>
              </th>
              <th className="text-left px-3 py-3 font-normal align-middle min-w-[140px]">
                <span className="inline-flex items-center whitespace-nowrap uppercase font-mono tracking-widest text-[0.625rem] text-muted-foreground">
                  Type
                </span>
              </th>
              <SortableTh colKey="stage" testId="sort-stage" align="left" className="min-w-[120px]">Stage</SortableTh>
              <SortableTh colKey="status" testId="sort-status" align="left" className="min-w-[110px]">Status</SortableTh>
              <SortableTh colKey="health" testId="sort-health" align="center" className="w-16">Health</SortableTh>
              <SortableTh colKey="playbook" testId="playbook-column-sort" align="left" className="w-[140px]">Playbook</SortableTh>
              <SortableTh colKey="budget" testId="sort-budget" align="right" className="pr-5 w-[160px]">Budget · Billed</SortableTh>
            </tr>
          </thead>
          <tbody>
            {sortedFiltered.length === 0 && <tr><td colSpan="9" className="px-5 py-12 text-center text-muted-foreground text-xs">No engagements yet.</td></tr>}
            {sortedFiltered.map((e) => {
              const pbPct = playbookCompletionPct(e);
              return (
              <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/30">
                <td className="px-5 py-3 align-middle max-w-[300px]">
                  <Link
                    to={`/engagements/${e.id}`}
                    className="font-medium hover:text-primary block truncate"
                    title={e.name}
                    data-testid={`engagement-row-${e.id}`}
                  >
                    {e.name}
                  </Link>
                </td>
                <td className="px-3 py-3 text-xs align-middle max-w-[180px]">
                  <Link
                    to={`/clients/${e.client_id}`}
                    className="hover:text-primary block truncate"
                    title={clientName(e.client_id)}
                  >
                    {clientName(e.client_id)}
                  </Link>
                </td>
                {/*
                  Partner lead — inline dropdown of canonical Users. Legacy
                  free-text `partner_lead` values that don't match any user's
                  name are still displayed as the trigger placeholder so we
                  never silently drop historical ownership data.
                */}
                <td className="px-3 py-3 text-xs align-middle max-w-[160px]">
                  <Select
                    value={
                      users.some((u) => u.name === e.partner_lead)
                        ? e.partner_lead
                        : "__unassigned"
                    }
                    onValueChange={(v) => patchEngagement(e.id, { partner_lead: v === "__unassigned" ? null : v })}
                  >
                    <SelectTrigger
                      onClick={(ev) => ev.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs hover:text-primary"
                      data-testid={`engagement-list-partner-lead-${e.id}`}
                    >
                      <SelectValue placeholder={e.partner_lead || "Unassigned"}>
                        <span className="truncate block">{e.partner_lead || "Unassigned"}</span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unassigned">Unassigned</SelectItem>
                      {users.map((u) => (
                        <SelectItem key={u.id} value={u.name} data-testid={`engagement-list-partner-lead-${e.id}-option-${u.id}`}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 text-xs align-middle max-w-[180px]" title={TYPE_LABEL[e.engagement_type] || e.engagement_type}>
                  <Select
                    value={e.engagement_type}
                    onValueChange={(v) => patchEngagement(e.id, { engagement_type: v })}
                  >
                    <SelectTrigger
                      onClick={(ev) => ev.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase font-mono tracking-widest hover:text-primary"
                      data-testid={`engagement-list-type-${e.id}`}
                    >
                      <SelectValue>
                        <span className="truncate block">{TYPE_LABEL[e.engagement_type] || e.engagement_type}</span>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 align-middle whitespace-nowrap">
                  <Select
                    value={e.stage}
                    onValueChange={(v) => patchEngagement(e.id, { stage: v })}
                  >
                    <SelectTrigger
                      onClick={(ev) => ev.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase font-mono tracking-widest hover:text-primary"
                      data-testid={`engagement-list-stage-${e.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENGAGEMENT_STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 align-middle whitespace-nowrap">
                  <Select
                    value={e.status}
                    onValueChange={(v) => patchEngagement(e.id, { status: v })}
                  >
                    <SelectTrigger
                      onClick={(ev) => ev.stopPropagation()}
                      className="rounded-none bg-transparent border-0 p-0 h-auto text-xs uppercase font-mono tracking-widest hover:text-primary"
                      data-testid={`engagement-list-status-${e.id}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ENGAGEMENT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-3 py-3 align-middle text-center">
                  <div className="inline-flex items-center gap-1.5">
                    <span className={`dot dot-${e.health} inline-block`} />
                    <Select
                      value={e.health || "green"}
                      onValueChange={(v) => patchEngagement(e.id, { health: v })}
                    >
                      <SelectTrigger
                        onClick={(ev) => ev.stopPropagation()}
                        className="rounded-none bg-transparent border-0 p-0 h-auto text-[0.625rem] uppercase font-mono tracking-widest hover:text-primary"
                        data-testid={`engagement-list-health-${e.id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ENGAGEMENT_HEALTHS.map((h) => <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </td>
                <td className="px-3 py-3 align-middle" data-testid={`engagement-playbook-${e.id}`}>
                  {pbPct === null ? (
                    <span className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground/60 whitespace-nowrap">Not opened</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-secondary min-w-[40px]">
                        <div className={`h-full ${pbPct === 100 ? "bg-primary" : "bg-primary/70"}`} style={{ width: pbPct + "%" }} />
                      </div>
                      <span className="text-[0.625rem] font-mono text-primary w-8 text-right tabular-nums">{pbPct}%</span>
                    </div>
                  )}
                </td>
                <td className="px-5 py-3 text-right data-cell align-middle whitespace-nowrap tabular-nums">
                  {fmt$(e.budget||0)} <span className="text-muted-foreground">/ {fmt$(e.billed_to_date||0)}</span>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
